import { Route, RouteObject, Routes, useLocation } from 'react-router-dom';
import { Suspense, useEffect, useMemo, useState } from 'react';
import AppRoutes from './AppRoutes';
import './scss/App.scss';
import './fonts/Montserrat-Medium.ttf';
import FullPageSpinner from 'components/common/FullPageSpinner/FullPageSpinner';
import { useGetReactConfigQuery } from './configApi';
import { setIntelligentServicesConfig } from './IntelligentServicesConfig';
import { initGtm } from './analytics/gtm';

const normalizePath = (p: string) => (p.length > 1 ? p.replace(/\/+$/, '') : p);
const normalizeTargetPath = (targetPath: string) => {
  const target = targetPath.trim();
  if (!target) return undefined;

  const withLeadingSlash = target.startsWith('/') ? target : `/${target}`;
  const normalized = normalizePath(withLeadingSlash);

  if (normalized === '/') return undefined;
  return normalized;
};

const findRouteElementByPath = (
  routes: RouteObject[],
  targetPath: string
): RouteObject['element'] | undefined => {
  const target = normalizePath(targetPath);

  for (const r of routes) {
    const p = typeof r.path === 'string' ? normalizePath(r.path) : undefined;

    if (p === target || p === `${target}/*`) {
      return r.element;
    }

    if (r.children?.length) {
      const childHit = findRouteElementByPath(r.children, targetPath);
      if (childHit) return childHit;
    }
  }

  return undefined;
};

export const App = () => {
  const [isConfigReady, setIsConfigReady] = useState(false);
  const location = useLocation();
  const {
    data: reactConfig,
    isLoading: isLoadingReactConfig,
    error: reactConfigError,
  } = useGetReactConfigQuery();

  const rootAliasTargetPath = useMemo(() => {
    const currentHost = window.location.hostname.toLowerCase();

    for (const rule of reactConfig?.rootRedirectRules ?? []) {
      const isEnabled = rule.enabled ?? true;
      if (!isEnabled) continue;

      const targetPath = normalizeTargetPath(rule.targetPath ?? '');
      if (!targetPath) continue;

      const hosts = (rule.hosts ?? [])
        .map((h) => h.trim().toLowerCase())
        .filter(Boolean);

      if (hosts.includes(currentHost)) {
        return targetPath;
      }
    }

    // Backward-compatible fallback for existing single-target config.
    const hostListRaw = (reactConfig?.rootRedirectHost ?? '').trim();
    const targetRaw = (reactConfig?.rootRedirectTargetPath ?? '').trim();

    const hosts = hostListRaw
      .split(/[,;\s]+/)
      .map((h) => h.trim().toLowerCase())
      .filter(Boolean);

    if (hosts.includes(currentHost)) {
      const targetPath = normalizeTargetPath(
        targetRaw.length > 0 ? targetRaw : '/ansokan'
      );
      return targetPath;
    }

    return undefined;
  }, [
    reactConfig?.rootRedirectRules,
    reactConfig?.rootRedirectHost,
    reactConfig?.rootRedirectTargetPath,
  ]);

  const shouldAliasRootToTarget = useMemo(() => {
    if (location.pathname !== '/') return false;
    return Boolean(rootAliasTargetPath);
  }, [location.pathname, rootAliasTargetPath]);

  const rootAliasedElement = useMemo(() => {
    if (!shouldAliasRootToTarget) return undefined;
    if (!rootAliasTargetPath) return undefined;

    return findRouteElementByPath(
      AppRoutes as RouteObject[],
      rootAliasTargetPath
    );
  }, [shouldAliasRootToTarget, rootAliasTargetPath]);

  useEffect(() => {
    if (!reactConfig) {
      setIsConfigReady(false);
      return;
    }
    setIntelligentServicesConfig(reactConfig);
    setIsConfigReady(true);
  }, [reactConfig]);

  useEffect(() => {
    initGtm(reactConfig?.gtmContainerId);
  }, [reactConfig?.gtmContainerId]);

  if (reactConfigError) {
    return <div>Det gick inte att läsa konfigurationen.</div>;
  }

  if (isLoadingReactConfig || !reactConfig || !isConfigReady) {
    return <FullPageSpinner loadingText="Laddar" />;
  }

  return (
    <Suspense fallback={<FullPageSpinner loadingText="Laddar" />}>
      <Routes>
        {AppRoutes.map((route, index) => {
          const { element, ...rest } = route;
          const isRootRoute =
            rest.index === true || rest.path === '/' || rest.path === '';

          // Alias "/" -> "/ansokan" (utan att ändra URL)
          if (isRootRoute && rootAliasedElement) {
            return <Route key={index} {...rest} element={rootAliasedElement} />;
          }

          return <Route key={index} {...rest} element={element} />;
        })}
      </Routes>
    </Suspense>
  );
};
